import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageMeta } from '@/components/PageMeta';
import { Reveal } from '@/components/Reveal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useAuth } from '@/hooks/useAuth';
import { useLaunchPromo } from '@/hooks/useLaunchPromo';
import {
  Camera, FileText, Languages, Globe, Smartphone, CheckCircle2,
  ArrowRight, Home, Sparkles, Share2, Wand2, Sofa, Video,
  TrendingUp, Check, X, Clock, Upload, BadgeCheck, KeyRound, Plane, Star,
} from 'lucide-react';
import { PhotoComparisonSlider } from '@/components/PhotoComparisonSlider';

import clutterBefore from '@/assets/clutter-before.jpg';
import clutterAfter from '@/assets/clutter-after.jpg';
import goldenHourBefore from '@/assets/golden-hour-before.jpg';
import goldenHourAfter from '@/assets/golden-hour-after.jpg';
import heroAfter from '@/assets/hero-after.jpg';
import stagingBefore from '@/assets/staging-before.jpg';
import stagingAfter from '@/assets/staging-after.jpg';

/** Shared premium dark-section background + a brighter on-dark accent color. */
const DARK_SECTION =
  'relative overflow-hidden bg-gradient-to-b from-[hsl(222_47%_12%)] via-[hsl(221_46%_9%)] to-[hsl(222_48%_7%)]';
const ACCENT = 'text-[hsl(205_100%_78%)]';

export default function SinglePropertyKit() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const promo = useLaunchPromo();
  const KIT_REGULAR_USD = 15;
  const KIT_PROMO_USD = 15;
  const kitPromo = promo.kitFirstTime;
  const displayKitPrice = kitPromo ? KIT_PROMO_USD : KIT_REGULAR_USD;

  const handleGetStarted = () => {
    navigate(user ? '/' : '/auth');
  };

  const features = [
    { icon: Camera, title: 'AI Photo Enhancement', description: 'Up to 10 AI-enhanced photos included (upgrade to 20 for just $5). Automatic brightness, colour correction, and decluttering.' },
    { icon: Wand2, title: 'AI Additions', description: 'Add swimming pools, patios, landscaping, and more to your photos with custom AI edits.' },
    { icon: Sofa, title: 'Virtual Staging', description: 'Virtually furnish empty rooms with tasteful decor to help buyers visualise the potential of your space.' },
    { icon: Sparkles, title: 'Premium AI Descriptions', description: 'Conversion-optimized property descriptions using research-backed keywords proven to boost sale prices.' },
    { icon: FileText, title: 'Multi-Platform Descriptions', description: 'Professionally written copy optimised for Facebook Marketplace, Instagram, and your property microsite.' },
    { icon: Languages, title: '28+ Language Translations', description: 'Reach international buyers with instant translations of your listing into 28+ languages.' },
    { icon: Globe, title: 'Shareable Microsite & QR Code', description: 'A beautiful, shareable property page with all photos, descriptions, and a QR code for print materials.' },
    { icon: Video, title: 'Video Slideshow', description: 'An automatically generated cinematic video slideshow of your property photos, ready to share.' },
    { icon: Smartphone, title: 'PDF Flyer', description: 'A professionally designed PDF flyer with your property details, photos, and QR code — ready to print or share.' },
    { icon: Share2, title: 'Social Media Content', description: 'Ready-to-post captions and stories for Instagram and Facebook, complete with relevant hashtags.' },
  ];

  const highlights = [
    { icon: Clock, label: 'Ready in ~10 min' },
    { icon: BadgeCheck, label: 'One-time payment' },
    { icon: Camera, label: '10 AI photos' },
    { icon: Globe, label: '28 languages' },
    { icon: Home, label: 'No agent needed' },
  ];

  const steps = [
    {
      num: '1',
      title: 'Tell Us About Your Property',
      desc: 'Fill in the basics — type, bedrooms, bathrooms, price, and location. It takes about a minute.',
      img: heroAfter,
      overlay: (
        <div className="absolute inset-0 flex items-center justify-center p-5">
          <div className="w-full max-w-[16rem] rounded-2xl bg-black/45 backdrop-blur-md ring-1 ring-white/20 p-4 space-y-2.5">
            {[['Type', 'House'], ['Bedrooms', '3'], ['Bathrooms', '2'], ['Price', '$450,000']].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-[11px]">
                <span className="text-white/60">{k}</span>
                <span className="text-white font-medium">{v}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      num: '2',
      title: 'Upload Your Photos',
      desc: 'Add up to 10 photos — phone shots are perfectly fine. Our AI enhances and stages them instantly.',
      img: stagingBefore,
      overlay: (
        <div className="absolute inset-0 flex items-center justify-center p-5">
          <div className="w-full max-w-[15rem] rounded-2xl bg-black/45 backdrop-blur-md ring-1 ring-white/20 p-5 text-center space-y-2">
            <Upload className="w-7 h-7 text-white mx-auto" />
            <p className="text-white text-sm font-medium">Upload up to 10 photos</p>
            <p className="text-white/60 text-[11px]">AI enhances them instantly</p>
          </div>
        </div>
      ),
    },
    {
      num: '3',
      title: 'Get Your Complete Kit',
      desc: 'Enhanced photos, descriptions, a microsite, flyer, and video — all generated and ready to share in about 10 minutes.',
      img: stagingAfter,
      overlay: (
        <>
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur text-white text-[11px] font-medium">
            <Sparkles className={`w-3.5 h-3.5 ${ACCENT}`} /> Your kit is ready
          </div>
          <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5">
            {['Photos', 'Descriptions', 'Microsite', 'Flyer', 'Video'].map((f) => (
              <span key={f} className="text-[10px] px-2 py-1 rounded-full bg-black/50 backdrop-blur text-white/90">{f}</span>
            ))}
          </div>
        </>
      ),
    },
  ];

  const whyOwners = [
    { icon: TrendingUp, title: 'Stand out from the crowd', description: 'Most private listings use phone photos and generic copy. AI-enhanced photos and professional descriptions make yours look polished and trustworthy.' },
    { icon: Globe, title: 'Reach more buyers', description: 'Your listing is translated into 28+ languages, shared via a professional microsite, and formatted for Facebook, Instagram, and marketplace platforms.' },
    { icon: Home, title: 'Everything in one place', description: 'Enhanced photos, descriptions, flyer, video, microsite, and social content — all generated from your property details in one go.' },
    { icon: Sparkles, title: 'No technical skills needed', description: 'Upload your photos, fill in the basics, and the AI does the rest. No design or copywriting experience required.' },
    { icon: KeyRound, title: 'Perfect for rentals too', description: 'Renting your place out? The same tools attract quality tenants with professional marketing materials.' },
    { icon: Share2, title: 'Share anywhere instantly', description: 'A unique microsite link, a QR code for print, and ready-to-post social content — all in one place.' },
  ];

  const personas = [
    { icon: Home, title: 'Homeowners selling privately', desc: 'Create a professional listing that stands out — enhanced photos, compelling descriptions, and a complete marketing package.' },
    { icon: KeyRound, title: 'Landlords listing a rental', desc: 'Attract quality tenants faster with enhanced photos, professional copy, and a shareable link you can post anywhere.' },
    { icon: Plane, title: 'Holiday & Airbnb owners', desc: 'Make your vacation rental stand out with stunning photos and multi-language descriptions that reach international guests.' },
    { icon: Sparkles, title: 'First-time listers', desc: 'No experience needed. The AI handles photography, copywriting, and marketing — you just fill in the details.' },
  ];

  const comparison = {
    columns: ['Estate agent', 'Hire freelancers', 'Single Property Kit'],
    rows: [
      { label: 'Professional listing photos', values: ['Included', '$150–300 / shoot', 'Up to 10 included'] },
      { label: 'Virtual staging', values: ['Rarely', '$30 / photo', 'Included'] },
      { label: 'Descriptions for every platform', values: ['Included', 'Copywriter fee', '5 formats, instant'] },
      { label: 'Microsite, flyer & video', values: ['Sometimes', 'Extra for each', 'All included'] },
      { label: '28-language translation', values: [false, false, 'Included'] },
      { label: 'Ready in', values: ['Days–weeks', 'Days–weeks', '~10 minutes'] },
      { label: 'Commission on sale', values: ['1.5–6%', 'None', 'None'] },
      { label: 'What you pay', values: ['Thousands', '$300–600+', `One-time $${displayKitPrice}`] },
    ],
  };

  const faqs = [
    { q: 'Is the Single Property Kit really a one-time payment?', a: `Yes. It's a single one-time purchase of $${displayKitPrice} for one property — no subscription, no recurring fees, and no commission on your sale. Everything in the kit is yours to keep.` },
    { q: 'Do I need any design or technical skills?', a: 'None at all. Enter a few property details, upload your photos, and the AI generates enhanced photos, descriptions, a microsite, flyer, and video automatically in about 10 minutes.' },
    { q: 'Will the AI-enhanced photos look fake?', a: 'No. The AI preserves your room layout, windows, and architecture — only lighting, clutter, and (optionally) staging change. Your original photos are always kept so you can disclose virtually staged shots.' },
    { q: 'How many photos can I enhance?', a: 'Up to 10 enhanced photos are included. Need more? Upgrade to 20 photos for just $5.' },
    { q: 'Can I edit the descriptions and re-roll photos?', a: 'Yes — every description is fully editable, and you can re-roll any photo result until you are happy with it.' },
    { q: 'Does it work for rentals and Airbnb?', a: 'Absolutely. The same tools work beautifully for rental and holiday listings — multi-language descriptions help you reach international tenants and guests.' },
    { q: 'What do I actually do with the kit?', a: 'Share your microsite link, print the flyer and QR code, post the ready-made social content, and paste your photos and descriptions into Facebook Marketplace, Idealista, Zillow, and any portal you like.' },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'ListBright Single Property Kit',
      image: [
        'https://listbright.ai/og/single-property-kit-1x1.jpg',
        'https://listbright.ai/og/single-property-kit-4x3.jpg',
        'https://listbright.ai/og/single-property-kit-16x9.jpg',
      ],
      sku: 'LB-SPK-001',
      url: 'https://listbright.ai/single-property-kit',
      description: 'A complete one-time listing kit for property owners: AI-enhanced photos, virtual staging, descriptions in 28+ languages, a shareable microsite, PDF flyer, and video — no subscription, no commission.',
      brand: { '@type': 'Brand', name: 'ListBright' },
      offers: {
        '@type': 'Offer',
        price: Number(displayKitPrice).toFixed(2),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        priceValidUntil: '2027-12-31',
        seller: { '@type': 'Organization', name: 'ListBright' },
        url: 'https://listbright.ai/single-property-kit',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question', name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="Single Property Kit — AI Listing Suite | ListBright"
        description="All-in-one AI listing kit for one home: photos, virtual staging, descriptions in 28+ languages, video, microsite and PDF flyer. One-time payment."
        canonical="https://listbright.ai/single-property-kit"
        breadcrumbs={[{ label: 'Single Property Kit', path: '/single-property-kit' }]}
        jsonLd={jsonLd}
      />

      {/* ── Hero (split: copy + pricing card) ── */}
      <section className="relative overflow-hidden border-b border-border/40 py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />
        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute top-[-6rem] right-1/4 w-[28rem] h-[28rem] bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-[-8rem] left-1/4 w-80 h-80 bg-accent/40 rounded-full blur-3xl pointer-events-none" />

        <div className="container max-w-6xl mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-6">
              <Badge variant="secondary" className="bg-primary/10 text-primary border-0 px-4 py-1.5 animate-fade-in">
                <Home className="w-3.5 h-3.5 mr-1.5" /> For Property Owners
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05] animate-fade-up">
                Sell Your Home Like a Pro —{' '}
                <span className="text-gradient">Without the Agent</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl animate-fade-up" style={{ animationDelay: '80ms' }}>
                The <strong className="text-foreground">Single Property Kit</strong> gives homeowners,
                landlords, and holiday-rental owners the exact tools agents use — AI-enhanced photos,
                staging, compelling descriptions, and a shareable microsite — for one small one-time
                payment. No subscription, no commission.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button size="lg" variant="hero" onClick={handleGetStarted} className="group shadow-lg">
                  Get Started <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button asChild size="lg" variant="outline"><Link to="/pricing">See Pricing</Link></Button>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground pt-1">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> One-time payment</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> No commission</span>
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-primary fill-primary" /> Ready in ~10 min</span>
              </div>
            </div>

            {/* Pricing card */}
            <Reveal delay={120} className="relative">
              <div className="absolute -inset-5 bg-gradient-to-tr from-primary/20 via-primary/10 to-transparent blur-3xl rounded-[2.5rem] opacity-70 pointer-events-none" />
              <div className="relative rounded-3xl border border-primary/20 bg-card shadow-elevated p-7 sm:p-8">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3">Single Property Kit</div>
                <div className="flex items-end gap-2 mb-1">
                  {kitPromo && <span className="text-xl text-muted-foreground line-through mb-1.5">${KIT_REGULAR_USD}</span>}
                  <span className="text-5xl font-display font-bold text-foreground">${displayKitPrice}</span>
                  <span className="text-muted-foreground mb-1.5">one-time</span>
                </div>
                <p className="text-sm text-muted-foreground mb-5">No subscription · No commission · One property, fully loaded</p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    '10 AI-enhanced photos (20 for +$5)',
                    'Virtual staging & AI photo edits',
                    'Premium + multi-platform descriptions',
                    'Translations in 28+ languages',
                    'Shareable microsite & QR code',
                    'PDF flyer & cinematic video',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm">
                      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
                <Button size="lg" variant="hero" onClick={handleGetStarted} className="w-full group shadow-lg">
                  Get Started <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
                {kitPromo && (
                  <p className="text-xs text-center text-primary font-medium mt-3">
                    First-listing price — sign up to claim
                  </p>
                )}
              </div>
            </Reveal>
          </div>

          {/* Highlights strip */}
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {highlights.map(({ icon: Icon, label }, i) => (
              <Reveal key={label} delay={i * 60} className="flex items-center gap-2.5 justify-center sm:justify-start rounded-xl border border-border/50 bg-card/50 px-4 py-3">
                <Icon className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">{label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Before / after ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
              <Sparkles className="w-4 h-4 text-primary" /> AI photo enhancement
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">See the Difference</h2>
            <p className="text-muted-foreground text-lg">
              The AI turns ordinary phone photos into professional-quality images that attract more buyers. Drag to compare.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {[{ b: clutterBefore, a: clutterAfter }, { b: goldenHourBefore, a: goldenHourAfter }].map((pair, i) => (
              <Reveal key={i} delay={i * 100} className="rounded-2xl overflow-hidden shadow-elevated border border-border/50">
                <PhotoComparisonSlider originalSrc={pair.b} enhancedSrc={pair.a} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works — dark stepped cards ── */}
      <section className={`${DARK_SECTION} py-20 lg:py-28`}>
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(0_0%_100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0_0%_100%) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute top-0 right-1/4 w-[30rem] h-[30rem] bg-primary/15 rounded-full blur-3xl pointer-events-none" />
        <div className="container max-w-6xl mx-auto px-4 relative">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="secondary" className={`bg-primary/15 ${ACCENT} border-0 ring-1 ring-primary/25 px-4 py-1.5 mb-4`}>
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> How it works
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-3">
              Your Listing, Easy as 1, 2, 3
            </h2>
            <p className="text-white/65 text-lg">
              No agent, no photographer, no design skills — just three simple steps.
            </p>
          </Reveal>

          <div className="space-y-6">
            {steps.map((step, i) => (
              <Reveal key={step.num} delay={i * 80}>
                <div className="grid md:grid-cols-2 gap-6 lg:gap-10 items-center rounded-3xl ring-1 ring-white/10 bg-white/[0.04] p-6 lg:p-10">
                  <div className={`space-y-5 ${i % 2 === 1 ? 'md:order-2' : ''}`}>
                    <div className="w-11 h-11 rounded-full bg-white text-[hsl(222_47%_11%)] flex items-center justify-center font-display font-bold text-lg">
                      {step.num}
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-display font-bold text-white">{step.title}</h3>
                    <p className="text-white/65 leading-relaxed max-w-md">{step.desc}</p>
                  </div>
                  <div className={`relative rounded-2xl overflow-hidden ring-1 ring-white/10 aspect-[16/11] ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                    <img src={step.img} alt={step.title} loading="lazy" className="w-full h-full object-cover" />
                    {step.overlay}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Everything you get ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
              <BadgeCheck className="w-4 h-4 text-primary" /> What's included
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">Everything You Get</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              One purchase — all the tools a professional agent uses to create listings that sell.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map(({ icon: Icon, title, description }, i) => (
              <Reveal key={title} delay={(i % 4) * 70}>
                <div className="card-premium h-full rounded-2xl border border-border/60 bg-card p-5 space-y-2.5 group">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center transition-colors group-hover:bg-primary/20">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-sm">{title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why owners choose it ── */}
      <section className="py-20 lg:py-28 border-b border-border/40 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">Why Owners Choose the Kit</h2>
            <p className="text-muted-foreground text-lg">
              Selling your home, renting an apartment, or listing a holiday property — the right presentation makes all the difference.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyOwners.map(({ icon: Icon, title, description }, i) => (
              <Reveal key={title} delay={(i % 3) * 80}>
                <div className="card-premium h-full rounded-2xl border border-border/60 bg-card p-6 space-y-3 group">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center transition-colors group-hover:bg-primary/20">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Comparison table ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <Badge variant="outline" className="mb-4">The math</Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">Keep the Commission in Your Pocket</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              The same pro-grade marketing an agent provides — without the agent's percentage of your sale price.
            </p>
          </Reveal>

          <Reveal className="overflow-x-auto">
            <div className="min-w-[640px] rounded-2xl border border-border/60 overflow-hidden">
              <div className="grid grid-cols-4 bg-muted/40">
                <div className="p-4 sm:p-5 text-sm font-semibold text-muted-foreground">What you get</div>
                {comparison.columns.map((col, i) => {
                  const isBrand = i === comparison.columns.length - 1;
                  return (
                    <div key={col} className={`p-4 sm:p-5 text-center text-sm font-bold ${isBrand ? 'bg-primary text-primary-foreground' : 'text-foreground'}`}>
                      {col}
                    </div>
                  );
                })}
              </div>
              {comparison.rows.map((row, ri) => (
                <div key={row.label} className={`grid grid-cols-4 ${ri % 2 === 1 ? 'bg-muted/20' : 'bg-card'}`}>
                  <div className="p-4 sm:p-5 text-sm font-medium text-foreground flex items-center">{row.label}</div>
                  {row.values.map((val, vi) => {
                    const isBrand = vi === row.values.length - 1;
                    return (
                      <div key={vi} className={`p-4 sm:p-5 text-center text-sm flex items-center justify-center gap-1.5 ${isBrand ? 'bg-primary/5 font-semibold text-foreground' : 'text-muted-foreground'}`}>
                        {val === false ? (
                          <X className="w-4 h-4 text-destructive/60" />
                        ) : isBrand ? (
                          <><Check className="w-4 h-4 text-primary shrink-0" /> {val}</>
                        ) : (
                          val
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="text-center mt-10">
            <Button size="lg" variant="hero" onClick={handleGetStarted} className="group shadow-lg">
              Get Started — One-time ${displayKitPrice}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ── Who is this for ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">Who Is This For?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">If you're putting a property in front of buyers or renters, the kit is for you.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {personas.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={(i % 4) * 70}>
                <div className="card-premium h-full rounded-2xl border border-border/60 bg-card p-6 space-y-3">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-display font-bold text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ (two-column) ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16">
            <Reveal className="lg:sticky lg:top-24 self-start space-y-5">
              <Badge variant="outline"><Sparkles className="w-3.5 h-3.5 mr-1.5 text-primary" /> FAQ</Badge>
              <h2 className="text-3xl md:text-4xl font-display font-bold leading-tight">
                Frequently Asked Questions
              </h2>
              <p className="text-muted-foreground">
                Still have a question?{' '}
                <Link to="/contact" className="text-primary font-medium hover:underline">Contact us.</Link>
              </p>
            </Reveal>
            <Reveal delay={100}>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={f.q} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20 lg:py-28">
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="text-center space-y-6 p-10 sm:p-14 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20">
            <h2 className="text-3xl sm:text-4xl font-display font-bold">Ready to List Your Property?</h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-lg">
              Create a professional listing in minutes — one-time purchase, no subscription, no commission.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Button size="lg" variant="hero" onClick={handleGetStarted} className="group text-base px-8 shadow-lg">
                Get Started — ${displayKitPrice} <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <Link to="/pricing">Compare Plans</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground pt-2">
              Are you a real estate agent instead?{' '}
              <Link to="/real-estate-marketing-software" className="text-primary hover:underline font-medium">
                See our software for agents →
              </Link>
            </p>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
